import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { useInfiniteQuery } from '@tanstack/react-query';

import { ReferralFormMeta, ReferToFormValues } from 'components/modals/referral/SendReferralModal';
import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput from 'components/shared/activityInput/useActivityInput';
import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import { Actions } from 'components/shared/StandardModal';
import LoadingOverlay from 'components/shared/table/LoadingOverlay';
import { useForm } from 'context/form';
import Group from 'models/Group';
import GroupType from 'models/GroupType';
import LocationEpisode from 'models/LocationEpisode';
import { attachmentsQueryKeys, indexAttachments } from 'services/api/attachments';
import { useInfiniteGroups } from 'services/api/group';
import AcceptedPlanIcon from 'svg/AcceptedPlan';

import { ReferralSteps } from './helpers';
import { referToFormValidation } from './referralFormValidation';

type ReferToStepProps = {
  locationEpisode: LocationEpisode;
};

function useGetReviewAttachments({
  reviewId,
  locationEpisode,
  enabled,
}: {
  reviewId?: string;
  locationEpisode?: LocationEpisode;
  enabled?: boolean;
}) {
  const query = useInfiniteQuery({
    queryKey: attachmentsQueryKeys.index({ reviewId, include: 'blob' }),
    queryFn: ({ signal, pageParam }) =>
      indexAttachments(
        {
          reviewId,
          include: 'blob',
          page: pageParam,
          ...(locationEpisode ? { createdByAllowedGroup: [locationEpisode.groupId, locationEpisode.owner.id] } : {}),
        },
        signal
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined,
    enabled: enabled && !!reviewId,
  });

  const attachments = useMemo(() => query.data?.pages.flatMap((page) => page.data), [query.data]);

  return {
    query,
    attachments,
    isLoading: query.isFetching && !query.isFetchingNextPage,
  };
}

function AcceptsPlanTypeBadge({ name }: { name: string }) {
  return (
    <PlanTypeBadge>
      <AcceptedPlanIcon width={12} height={12} />
      <span>Accepts {name}</span>
    </PlanTypeBadge>
  );
}

export default function ReferToStep(props: ReferToStepProps) {
  const { locationEpisode } = props;

  const { errors, setMeta, setValue, values, dirty, meta, submitForm } = useForm<ReferToFormValues, ReferralFormMeta>();

  const stepValid = useMemo(() => referToFormValidation().isValidSync(values), [values]);

  const providerTypeOptions = locationEpisode.owner.client.managedProviderTypes;

  const asyncGroupOptions = useInfiniteGroups({
    clientIdOrNetworkClientId: locationEpisode.owner.clientId,
    type: values.providerType?.apiName,
    'id.not': locationEpisode.group?.id,
    include: 'healthPlanClassificationIds',
    sortBy: 'name asc',
    viewOnly: true,
  });

  const latestReferral = locationEpisode.latestReferralReview;
  const reviewId = latestReferral?.id;
  const shouldCarryOverAttachments = !!latestReferral && (latestReferral.rejected || latestReferral.canceled);
  const {
    query: attachmentsQuery,
    attachments: previousAttachments,
    isLoading,
  } = useGetReviewAttachments({
    reviewId,
    locationEpisode,
    enabled: shouldCarryOverAttachments,
  });

  const { note, setNote } = useActivityInput({ ...values.note });

  // Track the IDs of attachments that were initially carried over
  const carriedOverAttachmentIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (previousAttachments?.length) {
      setNote((prev) => ({ ...prev, attachments: previousAttachments }));
      // Store the IDs of carried-over attachments
      carriedOverAttachmentIds.current = new Set(previousAttachments.map((a) => a.id));
    }
  }, [previousAttachments, setNote]);

  useEffect(() => {
    setValue('note', note);

    // Set previousReviewId if we're carrying over attachments
    if (shouldCarryOverAttachments && reviewId) {
      setValue('previousReviewId', reviewId);
    }

    // Compute removed attachment IDs by comparing current attachments to initial carried-over set
    const currentAttachmentIds = new Set(note.attachments.map((a) => a.id));
    const removedIds = [...carriedOverAttachmentIds.current].filter((id) => !currentAttachmentIds.has(id));
    setValue('removedAttachmentIds', removedIds.length > 0 ? removedIds : undefined);
  }, [note, setValue, shouldCarryOverAttachments, reviewId]);

  const selectedProviderTypeDesignatesAcceptedPlans = !!values.providerType?.config.designatesAcceptedPlans;
  const acceptedPlanType = locationEpisode.planTypeClassification?.parentClassification;
  const acceptedPlanTypeEnabled = selectedProviderTypeDesignatesAcceptedPlans && acceptedPlanType;

  const isMultiStep = meta.steps && meta.steps.length > 1;

  return (
    <>
      <>
        <InputGroup title='Select provider type'>
          <Select<GroupType>
            placeholder='Select provider type'
            options={providerTypeOptions}
            getOptionValue={(prop) => prop.apiName}
            getOptionLabel={(prop) => prop.displayName}
            onChange={(option) => {
              setValue('providerType', option);
              setValue('group', null, { validate: false });
            }}
            value={values.providerType}
          />
        </InputGroup>
        <InputGroup
          title={values.providerType?.displayName}
          visible={!!values.providerType}
          error={errors.group}
          badge={acceptedPlanTypeEnabled && <AcceptsPlanTypeBadge name={acceptedPlanType.name} />}>
          <Select<Group>
            {...asyncGroupOptions}
            placeholder='Select a location'
            getOptionValue={(prop) => prop.id}
            getOptionLabel={(prop) => prop.name}
            formatOptionLabel={(prop: Group) => {
              if (acceptedPlanTypeEnabled && prop.hasHealthPlanClassification(acceptedPlanType.id))
                return (
                  <GroupOption>
                    {prop.name}
                    <AcceptedPlanIcon width={14} height={14} />
                  </GroupOption>
                );
              return prop.name;
            }}
            onChange={(change) => {
              setValue('group', change);
            }}
            value={values.group}
          />
          {values.group && (
            <Alert style={{ marginTop: 8 }}>
              <Alert.Text>
                {values.group.name} will be notified and added to coordinate around referral needs in Olio.
              </Alert.Text>
            </Alert>
          )}
        </InputGroup>

        <FieldLabel>Referral Notes (optional)</FieldLabel>
        <ActivityInputContainer>
          <ActivityInput
            locationEpisodeId={locationEpisode.id}
            values={note}
            setValues={setNote}
            onUploading={(uploading) => setMeta('isUploadingAttachments', uploading)}
            existingAttachmentsQuery={shouldCarryOverAttachments ? attachmentsQuery : undefined}
          />
          {isLoading && <LoadingOverlay />}
        </ActivityInputContainer>
      </>
      <StyledActions>
        <Button variant='ghost' type='button' onClick={() => meta.onCancel?.()}>
          Cancel
        </Button>
        <Button
          type='button'
          data-cy='clickable'
          disabled={!stepValid || !dirty || meta.isUploadingAttachments}
          loading={meta.isSubmitting}
          onClick={() => {
            if (isMultiStep) {
              meta.onNextStep?.(setMeta, meta.currentStep as ReferralSteps);
            } else {
              submitForm();
            }
          }}>
          {isMultiStep ? 'Next' : meta.submitText}
        </Button>
      </StyledActions>
    </>
  );
}

const PlanTypeBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const GroupOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
`;

const StyledActions = styled(Actions)`
  margin-top: 20px;
`;

const ActivityInputContainer = styled.div`
  position: relative;
`;
